import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import { AgentStatus, ClickToCallAgentOpenInputDto, OperationType, WorkStatus } from '@api/ecommerce-xp';
import { useDidMount, useShowHide, useWillUnmount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$hooks';
import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useLogger } from '@hooks';
import { modals } from '@modals';
import { useNavigation } from '@navigation';
import {
	useFetchClickToCallAgentOpen,
	useFetchClickToCallAllInternationalDialingPhoneCodes,
	useFetchClickToCallIsOpen,
	useFetchClickToCallStatus,
} from '@services';
import { saveSuccessServiceResponse, useServiceResponse } from '@store';

interface RequestContactOutput {
	resources: RequestContactResources;
	phone: string;
	indicative: string;
	isAgentOpen: ClickToCallAgent;
	showDisclaimer: boolean;
	newRequestContact: () => void;
	validateClickToCallAgentOpen: () => void;
	setPhone: (value: string) => void;
	setIndicative: (value: string) => void;
	setContactToCallCountry: (value: string) => void;
	hideDisclaimer: () => void;
}

interface RequestContactResources {
	disclaimer: string;
}

type ClickToCallAgent = 'Open' | 'Closed' | 'ErrorToCall' | 'None';
let intervalId: NodeJS.Timeout | undefined | number | ReturnType<typeof setInterval> = undefined;

export const useRequestContactHelper = (): RequestContactOutput => {
	const logger = useLogger();
	const dispatch = useDispatch();
	const t = useTranslation();
	const { show, hide } = useShowHide(modals.waitingForCall);
	const { gotoHome } = useNavigation();

	const [isAgentOpen, setIsAgentOpen] = useState<ClickToCallAgent>('None');
	const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);

	const [indicative, setIndicative] = useState<string>('');
	const [phone, setPhone] = useState<string>('');
	const [contactToCallCountry, setContactToCallCountry] = useState<string>('');

	const { fetch: fetchClickToCallStatus, data: clickToCallStatusData } = useFetchClickToCallStatus();
	const { fetch: fetchClickToCallAgentOpen, data: clickToCallAgentOpenData } = useFetchClickToCallAgentOpen();

	const { fetch: fetchRetrieveCountries } = useFetchClickToCallAllInternationalDialingPhoneCodes();
	const { fetch: fetchClickToCallIsOpen } = useFetchClickToCallIsOpen();
	const { clickToCallIsOpen, clickToCallAllInternationalDialingPhoneCodes } = useServiceResponse();

	const timeForInterval = useMemo(() => {
		const time = parseInt(t('placeholders.clickToCall.time'));
		logger('timeForInterval >', {
			timeOnSP: time,
			timeInMS: `${time} * 1000 = ${time * 1000}`,
			defaultTime: 10000,
		});
		return !isNaN(time) ? time * 1000 : 10000;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t]);

	useDidMount(() => {
		fetchClickToCallIsOpen({});
		if (clickToCallAllInternationalDialingPhoneCodes === undefined) {
			fetchRetrieveCountries({ operationType: OperationType.CODES_MB_WAY });
		}
	});

	useWillUnmount(() => {
		dispatch(saveSuccessServiceResponse({ clickToCallIsOpen: undefined }));
		clearCheckInterval();
	});

	useEffect(() => {
		logger('useEffect > clickToCallAgentOpenData', {
			clickToCallAgentOpenData,
		});
		if (intervalId === undefined && !CoreHelper.isNullOrEmpty(clickToCallAgentOpenData?.workItemId)) {
			setIntervalCall();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallAgentOpenData?.workItemId]);

	useEffect(() => {
		if (
			!CoreHelper.isNullOrEmpty(clickToCallStatusData?.workStatus) &&
			clickToCallStatusData?.workStatus !== WorkStatus.PROCESSING
		) {
			clearCheckInterval();
			if (clickToCallStatusData?.workStatus === WorkStatus.ANSWER) {
				gotoHome();
			} else {
				setIsAgentOpen('ErrorToCall');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallStatusData?.workStatus]);

	useEffect(() => {
		if (
			!CoreHelper.isNullOrEmpty(clickToCallIsOpen?.agentStatus) &&
			// !CoreHelper.isNullOrEmpty(retrieveCountriesData?.countries)
			!CoreHelper.isNullOrEmpty(clickToCallAllInternationalDialingPhoneCodes?.internationalPhoneCodes)
		) {
			logger('useEffect > clickToCallIsOpenData', {
				clickToCallIsOpen,
			});
			setIsAgentOpen(clickToCallIsOpen?.agentStatus === AgentStatus.OPEN ? 'Open' : 'Closed');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallIsOpen?.agentStatus, clickToCallAllInternationalDialingPhoneCodes?.internationalPhoneCodes?.length]);

	const resources = {
		disclaimer: t('placeholders.requestToCall.disclaimer'),
	};

	const validateClickToCallAgentOpen = () => {
		show();
		const data: ClickToCallAgentOpenInputDto = {
			phoneNumber: indicative + phone,
			phoneCountryCode: contactToCallCountry,
		};
		fetchClickToCallAgentOpen(data);
	};

	const clearCheckInterval = () => {
		if (intervalId !== undefined) {
			intervalId = undefined;
			clearInterval(intervalId);
			hide();
		}
	};

	const setIntervalCall = () => {
		intervalId = setInterval(() => {
			fetchClickToCallStatus({
				workItemId: clickToCallAgentOpenData?.workItemId,
			});
		}, timeForInterval);
	};

	const newRequestContact = useCallback(() => {
		setIsAgentOpen('Closed');
		setShowDisclaimer((oldValue) => !oldValue);
	}, []);

	const hideDisclaimer = () => setShowDisclaimer(false);

	return {
		resources,
		phone,
		indicative,
		isAgentOpen,
		showDisclaimer,
		validateClickToCallAgentOpen,
		setPhone,
		setIndicative,
		newRequestContact,
		setContactToCallCountry,
		hideDisclaimer,
	};
};
